import React, {
    useEffect,
    useRef
} from 'react';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { Box, Chip } from '@mui/material';

import type { TenantRoute } from '@types';
import { leafletPositions, routeColor } from '@utils/tenantRoutes';

type RoutesMapProps = {
    routes: TenantRoute[],
    height?: number | string
};

const RoutesMap: React.FC<RoutesMapProps> = (props) => {
    const { routes, height = 420 } = props;
    const mapRef = useRef<L.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const layersRef = useRef<L.LayerGroup | null>(null);
    const [activeId, setActiveId] = React.useState<number | null>(null);

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) {
            return undefined;
        }

        const map = L.map(mapContainerRef.current, {
            zoomControl: true,
            attributionControl: false,
            scrollWheelZoom: false
        }).setView([62, 15], 5);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: ' ',
            maxZoom: 19
        }).addTo(map);

        layersRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
            layersRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        const layers = layersRef.current;
        if (!map || !layers) return;

        layers.clearLayers();
        const bounds = L.latLngBounds([]);

        routes.forEach((route, index) => {
            const color = routeColor(index);
            const positions = leafletPositions(route.points);
            if (positions.length < 4) return;
            const isActive = activeId === route.id;
            const polygon = L.polygon(positions, {
                color,
                fillColor: color,
                fillOpacity: isActive ? 0.28 : 0.14,
                weight: isActive ? 2.5 : 1.5,
                opacity: activeId && !isActive ? 0.35 : 1
            });
            polygon.bindTooltip(route.name, { sticky: true });
            polygon.on('click', () => setActiveId(route.id));
            polygon.addTo(layers);
            positions.forEach((position) => bounds.extend(position));
        });

        if (bounds.isValid()) {
            if (activeId) {
                const active = routes.find((route) => route.id === activeId);
                if (active) {
                    const activeBounds = L.latLngBounds(leafletPositions(active.points));
                    if (activeBounds.isValid()) {
                        map.flyToBounds(activeBounds.pad(0.35), { duration: 0.6 });
                        return;
                    }
                }
            }
            map.fitBounds(bounds.pad(0.2));
        }

        setTimeout(() => map.invalidateSize(), 100);
    }, [routes, activeId]);

    if (routes.length === 0) {
        return null;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {routes.map((route, index) => {
                    const selected = activeId === route.id;
                    const color = routeColor(index);
                    return (
                        <Chip
                            key={route.id}
                            label={route.name}
                            onClick={() => setActiveId(selected ? null : route.id)}
                            sx={{
                                bgcolor: selected ? color : 'background.paper',
                                color: selected ? '#fff' : color,
                                border: `1px solid ${color}`,
                                fontWeight: 600
                            }}
                            variant={selected ? 'filled' : 'outlined'}
                        />
                    );
                })}
            </Box>
            <Box
                ref={mapContainerRef}
                sx={{
                    height,
                    width: '100%',
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
            />
        </Box>
    );
};

export default RoutesMap;
